/* eslint-disable no-underscore-dangle */
import { useEffect } from 'react';

import { usePageBuilder } from 'hooks/api/builder/usePageBuilder';

import { LightboxGallery } from '../LightboxGallery';
import { ComponentSettingsControl } from './editorControls/ComponentSettingsControl';
import { EditorBlockerModal } from './editorControls/EditorBlockerModal';
import { NodeTree } from './editorControls/NodeTree';
import { EditorPagePreviewControl } from './editorControls/PagePreviewControl';
import { PageSaverControl } from './editorControls/PageSaverControl';
import { PageSelectControl } from './editorControls/PageSelectControl';
import { PageSettingsControl } from './editorControls/PageSettingsControl';
import {
    PageEditorWrapper,
    EditorToolbarWrapper,
    EditorPreviewWrapper,
} from './PageEditor.styled';

export const PageContentEditor = (): JSX.Element => {
    const { pageList, page, undoRedoState, controls } = usePageBuilder();

    const {
        saveChanges,
        resetChanges,
        onNewComponentCreated,
        onNewPageCreated,
        onPageDeleted,
        page: { onPageConfigChange, onPageSettingsChange },
        node: { onNodeDelete, onNodeDuplicate, onNodeSettingsChange },
    } = controls;

    useEffect(() => {
        if (page) {
            document.body.setAttribute('data-page-id', String(page.config?.id));
        }
    }, [page]);

    return (
        <PageEditorWrapper>
            <EditorBlockerModal when={undoRedoState.hasChanges} />
            <EditorToolbarWrapper>
                <PageSelectControl
                    onNewPageAdded={onNewPageCreated}
                    selectionBlocked={undoRedoState.hasChanges}
                    pageList={pageList.pages || []}
                    error={pageList.error}
                    loading={pageList.loading}
                />
                {page.config ? (
                    <NodeTree
                        onNewComponentCreated={onNewComponentCreated}
                        onNodesChange={onPageConfigChange}
                    />
                ) : undefined}
            </EditorToolbarWrapper>
            <EditorPreviewWrapper>
                <EditorPagePreviewControl
                    undoRedoActions={undoRedoState}
                    loading={page.loading}
                    error={page.error}
                />
            </EditorPreviewWrapper>
            <EditorToolbarWrapper>
                {page.config ? (
                    <>
                        <PageSaverControl
                            hasChanges={undoRedoState.hasChanges}
                            onReset={() => resetChanges()}
                            onSave={() => saveChanges()}
                            loading={saveChanges.loading}
                            config={page.config}
                        />
                        <PageSettingsControl
                            onSettingsChange={onPageSettingsChange}
                            onDeleteSuccess={onPageDeleted}
                            config={page.config}
                            key={undoRedoState.key}
                        />
                        <ComponentSettingsControl
                            onNodeChange={onNodeSettingsChange}
                            onNodeDelete={onNodeDelete}
                            onNodeDuplicate={onNodeDuplicate}
                        />
                    </>
                ) : undefined}
            </EditorToolbarWrapper>
            <LightboxGallery />
        </PageEditorWrapper>
    );
};
